import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Stack, Typography, Box, Drawer, Fab } from '@mui/material';
import { useMCContext } from "./contexts/useMCStates";
import { useAuth } from "react-oidc-context";
import CreateApi from './Settings/appSettings';
import MenuSpanHeader from './Components/MenuSpanHeader';
import { useNavigate } from 'react-router-dom';
import { MCHeader } from './Components/MCHeader';
import MenuIcon from '@mui/icons-material/Menu';
import PullToRefresh from './services/PullToRefresh';
import axios from 'axios';
import { MCUserProfileSection } from './MCUserProfileSection';
import { MCRewardsCreditsSection } from './MCRewardsCreditsSection';

export const UserAccountSplash = () => {

    const { appTheme, windowSize, factor, setUserData } = useMCContext();
    const auth = useAuth();
    const api = CreateApi();
    const navigate = useNavigate();

    const [accountTabValue, setAccountTabValue] = useState<string>('profile');
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

    const stackRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (auth.isAuthenticated && auth.user?.profile?.sub) {
            const loadProfile = async () => {
                try {
                    const { data } = await api.get(`/myaccount/GetOwnProfile`);
                    if (data) {
                        setUserData(data);
                    }
                } catch (error) {
                    if (!axios.isAxiosError(error) || error.response?.status !== 404) {
                        console.error('Error loading profile:', error);
                    }
                }
            };
            void loadProfile();
        }
        // CreateApi() returns a fresh axios wrapper each render; including `api` would refetch endlessly.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sub + auth + setUserData are stable triggers only
    }, [auth.isAuthenticated, auth.user?.profile?.sub, setUserData]);

    const handleNavigateToSearch = useCallback(() => {
        navigate('/');
    }, [navigate]);

    const menuItems = useMemo(() => [
        {
            matchName: 'profile',
            label: 'Profile'
        },
        {
            matchName: 'rewards-credits',
            label: 'Rewards & Credits'
        },
        {
            matchName: 'preferences',
            label: 'Preferences'
        },
        {
            matchName: 'data-settings',
            label: 'Data Settings'
        },
        {
            matchName: 'event-search',
            label: 'Moment Central',
            onClick: handleNavigateToSearch
        }
    ], [handleNavigateToSearch]);

    if (!auth.isAuthenticated) return null;

    return (

        <Stack direction='column' width='100dvw' height='100dvh'
            sx={{
                WebkitOverflowScrolling: 'touch',
                WebkitOverscrollBehavior: 'none',
            }}
        >
            <PullToRefresh threshold={80} resistance={2}  containerRef={stackRef as React.RefObject<HTMLDivElement>} 
            onRefresh={() => {
                navigate(0);
                return Promise.resolve();
            }}>

            <MCHeader label='My Account' showCart={false} hamburgerFabVisible={windowSize.isMobile || windowSize.verticalPC !== 'none' || ['small'].includes(windowSize.horizontalPC)} />
            {(windowSize.isMobile || windowSize.verticalPC !== 'none' || ['small'].includes(windowSize.horizontalPC))  && (
                <Fab
                    onClick={() => setDrawerOpen(true)}
                    sx={{
                        position: 'absolute',
                        top: 10*factor,
                        left: 10*factor,
                        zIndex: 1200,
                        width: 'max-content',
                        height: 'max-content',
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                    }}
                >
                    <MenuIcon id="drawer-menu-icon" 
                        sx={{fontSize: 24*factor, borderRadius: '50%', 
                        padding: '8px', backgroundColor: 'primary.light', color: 'white', boxShadow: 'none' }} />
                </Fab>
            )}
            {(windowSize.isMobile || windowSize.verticalPC !== 'none' || ['small'].includes(windowSize.horizontalPC)) && (
                <Drawer anchor='left' variant='temporary' open={drawerOpen} onClose={() => {setDrawerOpen(false)}}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: 200,
                            boxSizing: 'border-box',
                        },
                    }}
                >              
                    <Stack direction={'column'} spacing={2} flex={1}>
                        <Stack direction={'column'} spacing={2} width={190} padding={'20px 0px'}
                            sx={{
                                borderRight: !drawerOpen ? `1px solid ${appTheme.palette.primary.light}` : 'none',
                                paddingRight: '1px'                        
                            }}>
                            {menuItems.map((item) => (
                                <MenuSpanHeader
                                    key={item.matchName}
                                    matchName={item.matchName}
                                    label={item.label}
                                    accountTabValue={accountTabValue}
                                    setViewEvent={() => {}}
                                    setAccountTabValue={setAccountTabValue}
                                    onClick={item.onClick}
                                />
                            ))}
                        </Stack>
                    </Stack>
                </Drawer>
            )}
            <Stack direction={'row'} spacing={2} flex={1} minHeight={0} ref={stackRef}>
                {!windowSize.isMobile && windowSize.verticalPC === 'none' && !['small'].includes(windowSize.horizontalPC) && (
                    <Stack direction={'column'} spacing={2} width={200} 
                    height={`calc(100dvh - ${60*factor}px - env(safe-area-inset-bottom))`}>
                        <Stack direction={'column'} spacing={2} flex={1} padding={'20px 0px'}
                            sx={{
                                borderRight: `1px solid ${appTheme.palette.primary.light}`,
                                paddingRight: '1px'                        
                            }}>
                            {menuItems.map((item) => (
                                <MenuSpanHeader
                                    key={item.matchName}
                                    matchName={item.matchName}
                                    label={item.label}
                                    accountTabValue={accountTabValue}
                                    setViewEvent={() => {}}
                                    setAccountTabValue={setAccountTabValue}
                                    onClick={item.onClick}
                                />
                            ))}
                        </Stack>
                    </Stack>
                )}
                {accountTabValue === 'profile' && (
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <MCUserProfileSection />
                    </Box>
                )}
                {accountTabValue === 'rewards-credits' && (
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <MCRewardsCreditsSection />
                    </Box>
                )}
                {accountTabValue === 'preferences' && (
                    <Box sx={{flex: 1, padding: 3}}>
                        <Typography variant="h6" sx={{fontWeight: 500, color: appTheme.palette.primary.main}}>
                            Preferences Functions Coming Soon!
                        </Typography>
                    </Box>
                )}
                {accountTabValue === 'data-settings' && (
                    <Box sx={{flex: 1, padding: 3}}>
                        <Typography variant="h6" sx={{fontWeight: 500, color: appTheme.palette.primary.main}}>
                            Data Settings Functions Coming Soon!
                        </Typography>
                    </Box>
                )}
            </Stack>
            </PullToRefresh>
        </Stack>
    ) 
}
